import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Text, Stack, TextField } from '@veeqo/ui';
import { useForm, Controller } from 'react-hook-form';
import InputGroup from 'components/common/InputGroup';
import { verifyCode } from 'utils/api';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'context/Context';
import { useTrackForm } from 'utils/adobeAnalytics/hooks/useTrackForm';
import { Form } from '../styled';

type FormData = {
  code: string;
};

type Response = {
  error?: boolean;
  isRetryable?: boolean;
  redirectTo?: string;
};

export const VerifyCodeForm = () => {
  const { actions, state } = useAppContext();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const [hasVerificationFailed, setHasVerificationFailed] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const { trackFormUpdated } = useTrackForm('signin-otp-form');

  const onSubmit = handleSubmit(async (data) => {
    setIsButtonLoading(true);
    const returnTo = state.signupData.return_to;
    const response: Response | undefined = await verifyCode({ ...data, return_to: returnTo });

    if (response?.error && response?.isRetryable) {
      setIsButtonLoading(false);
      setHasVerificationFailed(true);
      return;
    }

    if (response?.error) {
      actions.resetSignupData();
      actions.userNeedVerification(false);
      navigate('..', { replace: true });
    }

    if (response.redirectTo) {
      global.location.assign(decodeURIComponent(response.redirectTo));
    } else {
      Sentry.captureMessage('No redirect_to when verifying code');
    }
  });

  return (
    <Form onSubmit={onSubmit} id="signin-otp-form">
      <Stack spacing={6}>
        <InputGroup>
          <Controller
            name="code"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                onChange={onChange}
                onBlur={trackFormUpdated('code')}
                value={value}
                aria-label="code"
                type="text"
                label="Verification Code"
                placeholder="Enter code"
                autoComplete="one-time-code"
                error={errors.code && errors.code.message}
              />
            )}
            rules={{ required: 'Required' }}
          />
        </InputGroup>
        {hasVerificationFailed && (
          <Text variant="errorSmall">The code you entered was incorrect. Please try again</Text>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={isButtonLoading}
          disabled={isButtonLoading}
        >
          Verify
        </Button>
      </Stack>
    </Form>
  );
};
